import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import Address from '../Address/Address';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import { DateTimePicker } from '../Date/DateTimePicker';
import { JobWithHazardSurvey } from './HazardSurveyThirdPane';
import UserPicker from '../User/UserPicker';
import { useMutation } from '@apollo/client';
import { ASSIGN_JOB, SCHEDULE_JOB, UPDATE_HAZARD_SURVEY } from './queries';
import useNotify from '../../hooks/useNotify';
import CustomerPicker from '../Customer/CustomerPicker';
import { Input } from '../Form';
import StatusChip from '@/components/Status/StatusChip';

type HazardSurveyDetailProps = {
  job: JobWithHazardSurvey;
  onChange?: () => void;
};

const HazardSurveyDetail: React.FC<HazardSurveyDetailProps> = ({
  job,
  onChange,
}) => {
  const history = useHistory();
  const mode = useMode();
  const { showError, showSuccess } = useNotify();

  const [updateSurvey] = useMutation(UPDATE_HAZARD_SURVEY, {
    onCompleted: () => {
      showSuccess('Updated');
      onChange?.();
    },
    onError: (error) => {
      showError('Update failed', error);
    },
  });

  const [assignJob] = useMutation(ASSIGN_JOB, {
    onCompleted: () => {
      showSuccess('Assigned');
      onChange?.();
    },
    onError: (error) => {
      showError('Assignment failed', error);
    },
  });

  const [scheduleJob] = useMutation(SCHEDULE_JOB, {
    onCompleted: () => {
      showSuccess('Scheduled');
      onChange?.();
    },
    onError: (error) => {
      showError('Scheduling failed', error);
    },
  });

  const handleSave = async (field: string, value: any) => {
    if (field === 'assignedTo') {
      if (!value) {
        showError('Please select a user');
        return;
      }
      await assignJob({
        variables: {
          jobId: job.id,
          vendorProfileId: value,
        },
      });
    } else if (field === 'scheduled') {
      await scheduleJob({
        variables: {
          jobId: job.id,
          scheduled: value ? new Date(value).toISOString() : undefined,
        },
      });
    } else if (field === 'contact') {
      await updateSurvey({
        variables: {
          jobId: job.id,
          input: {
            contactProfileId: value,
          },
        },
      });
    }
  };

  const items = job.waterHazardSurvey?.items || [];

  return (
    <>
      <Accordion title="Water Service">
        <AccordionItem
          label="Service #"
          value={job.waterHazardSurvey?.waterService.siteNumber || '-'}
        />
        {job.waterHazardSurvey?.waterService.meterNumber && (
          <AccordionItem
            label="Meter #"
            value={job.waterHazardSurvey.waterService.meterNumber}
          />
        )}
        {job.waterHazardSurvey?.waterService.hazardSite?.name && (
          <AccordionItem
            label="Site Name"
            value={job.waterHazardSurvey.waterService.hazardSite.name}
          />
        )}
        {job.waterHazardSurvey?.waterService.hazardSite?.siteNumber && (
          <AccordionItem
            label="Site #"
            value={job.waterHazardSurvey.waterService.hazardSite.siteNumber}
          />
        )}
        <AccordionItem
          label="Address"
          value={<Address address={job.address} />}
        />
      </Accordion>
      <Accordion title="Visit">
        <AccordionItem
          label="Scheduled"
          value={
            <DateTimePicker
              selected={job.scheduled ? new Date(job.scheduled) : null}
              onChange={(date: string | null) => {
                handleSave('scheduled', date);
              }}
            />
          }
        />
      </Accordion>

      <Accordion title="Assigned to">
        <UserPicker
          userId={job.outboundAssignment?.vendorProfile.id}
          onChange={(user) => handleSave('assignedTo', user?.profileId)}
        />
      </Accordion>

      <Accordion title="Site Contact">
        <CustomerPicker
          customerProfileId={job.waterHazardSurvey?.contactProfileId}
          serviceAddressId={job.address.id}
          serviceAddressWhere={{
            active: true,
            addressId: job.address.id,
          }}
          onChange={(customer) => handleSave('contact', customer?.profileId)}
        />
        {job.waterHazardSurvey?.contact && (
          <>
            {job.waterHazardSurvey.contact.phones.length > 0 && (
              <AccordionItem
                value={job.waterHazardSurvey.contact.phones[0].value}
              />
            )}
            {job.waterHazardSurvey.contact.emails.length > 0 && (
              <AccordionItem
                value={job.waterHazardSurvey.contact.emails[0].value}
              />
            )}
          </>
        )}
      </Accordion>

      {items.length > 0 && (
        <Accordion title="Hazards to check">
          {items.map(({ waterHazard }) => (
            <StatusChip
              mr={1}
              mb={1}
              key={waterHazard.id}
              status={waterHazard.hazardLevel === 'HIGH' ? 'error' : 'warning'}
            >
              {waterHazard.name}
            </StatusChip>
          ))}
        </Accordion>
      )}
    </>
  );
};

HazardSurveyDetail.propTypes = {
  job: PropTypes.any,
};

export default HazardSurveyDetail;
